import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBatchAction } from "@/hooks/use-machines";
import { TOAST_SUCCESS_DURATION_MS } from "@/lib/toast-durations";
import type { BatchActionName, Machine } from "@/lib/types";
import { cn } from "@/lib/utils";

interface BatchProgressDialogProps {
  machines: Machine[];
  action: BatchActionName;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deleteAfterStop?: boolean;
}

interface BatchResult {
  id: number;
  ok: boolean;
  machine: Machine | null;
  error: string | null;
}

const ACTION_LABELS: Record<BatchActionName, { gerund: string; done: string; toast: string }> = {
  release: { gerund: "Releasing", done: "release", toast: "Released" },
  stop: { gerund: "Stopping", done: "stop", toast: "Stopped" },
  start: { gerund: "Starting", done: "start", toast: "Started" },
  delete: { gerund: "Deleting", done: "delete", toast: "Deleted" },
  reboot: { gerund: "Rebooting", done: "reboot", toast: "Rebooted" },
  skip_eod: { gerund: "Skipping EOD", done: "skip EOD", toast: "EOD skipped" },
  unskip_eod: { gerund: "Clearing EOD skip", done: "clear EOD skip", toast: "EOD skip cleared" },
  mark_disposable: {
    gerund: "Marking disposable",
    done: "mark disposable",
    toast: "Marked disposable",
  },
  unmark_disposable: {
    gerund: "Clearing disposable",
    done: "clear disposable",
    toast: "Disposable cleared",
  },
};

export function BatchProgressDialog({
  machines,
  action,
  open,
  onOpenChange,
  deleteAfterStop,
}: BatchProgressDialogProps) {
  const batch = useBatchAction();
  const [elapsedMs, setElapsedMs] = useState(0);
  const [results, setResults] = useState<BatchResult[] | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  // Snapshot the input list when the dialog opens so the in-flight render
  // doesn't go to "0 machines" when TanStack Query refetches and the
  // upstream selection filter sheds the just-leased rows.
  const [snapshot, setSnapshot] = useState<Machine[]>([]);

  const labels = ACTION_LABELS[action];
  const actionLabel = deleteAfterStop && action === "stop" ? "Stop+delete" : labels.gerund;
  const completionLabel = deleteAfterStop && action === "stop" ? "stop+delete" : labels.done;
  const toastVerb = deleteAfterStop && action === "stop" ? "Stopped + deleted" : labels.toast;

  useEffect(() => {
    if (open && snapshot.length === 0 && machines.length > 0) {
      setSnapshot(machines);
    }
  }, [open, snapshot.length, machines]);

  useEffect(() => {
    if (!batch.isPending) return;
    const startedAt = performance.now();
    const interval = setInterval(() => {
      setElapsedMs(performance.now() - startedAt);
    }, 100);
    return () => clearInterval(interval);
  }, [batch.isPending]);

  useEffect(() => {
    if (!open) {
      setElapsedMs(0);
      setResults(null);
      setBatchId(null);
      setSnapshot([]);
      batch.reset();
    }
  }, [open, batch]);

  // Auto-fire the batch once when the dialog opens (after snapshot lands).
  useEffect(() => {
    if (open && snapshot.length > 0 && results === null && !batch.isPending && !batch.isError) {
      batch.mutate(
        {
          machineIds: snapshot.map((machine) => machine.id),
          action,
          deleteAfterStop,
        },
        {
          onSuccess: (data) => {
            setResults(data.results);
            setBatchId(data.batch_id);
          },
        },
      );
    }
  }, [open, snapshot, results, batch, action, deleteAfterStop]);

  const isPending = batch.isPending;
  const isMutationError = batch.isError && results === null;
  const errorMessage = batch.error instanceof Error ? batch.error.message : null;
  const succeeded = results?.filter((result) => result.ok).length ?? 0;
  const failed = results ? results.length - succeeded : 0;
  const elapsedSeconds = Math.floor(elapsedMs / 1000);
  const resultsById = new Map(results?.map((result) => [result.id, result]));

  // Auto-close on clean success → 1-minute toast with the machine names.
  // Failures keep the dialog open for inspection.
  const autoCloseDoneRef = useRef(false);
  useEffect(() => {
    if (!results || autoCloseDoneRef.current || failed > 0) return;
    autoCloseDoneRef.current = true;
    const names = results
      .filter((result) => result.ok)
      .map(
        (result) =>
          result.machine?.name ??
          snapshot.find((machine) => machine.id === result.id)?.name ??
          `id=${result.id}`,
      );
    toast.success(`${toastVerb} ${names.length} machine${names.length === 1 ? "" : "s"}`, {
      description: names.join(", "),
      duration: TOAST_SUCCESS_DURATION_MS,
    });
    onOpenChange(false);
  }, [results, failed, snapshot, toastVerb, onOpenChange]);

  useEffect(() => {
    if (!open) autoCloseDoneRef.current = false;
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && isPending) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="border-border bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {results
              ? `Batch ${completionLabel} complete`
              : `${actionLabel} ${snapshot.length} machine${snapshot.length === 1 ? "" : "s"}`}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {results ? (
              <>
                <span className="text-emerald-600 dark:text-emerald-400">
                  {succeeded} succeeded
                </span>
                {failed > 0 && (
                  <>
                    {" · "}
                    <span className="text-destructive">{failed} failed</span>
                  </>
                )}
                {batchId && (
                  <span className="ml-2 font-mono text-[10px] uppercase tracking-wider">
                    batch:{batchId}
                  </span>
                )}
              </>
            ) : (
              <>Running in parallel — {elapsedSeconds}s elapsed.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {isMutationError && (
          <div className="flex items-start gap-2.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-foreground">Batch request failed</span>
              <span className="text-xs text-muted-foreground">
                {errorMessage ?? "Unknown error — check the network tab."}
              </span>
            </div>
          </div>
        )}

        <div className="flex max-h-[50vh] flex-col gap-1.5 overflow-y-auto pr-1">
          {snapshot.map((machine) => {
            const result = resultsById.get(machine.id);
            const isDone = result !== undefined;
            const isOk = isDone && result.ok;
            return (
              <div
                key={machine.id}
                className={cn(
                  "flex items-center gap-2.5 rounded-md border px-3 py-2 transition-colors",
                  !isDone && "border-primary/30 bg-primary/5",
                  isOk && "border-emerald-500/40 bg-emerald-500/5",
                  isDone && !isOk && "border-destructive/40 bg-destructive/5",
                )}
              >
                {isDone ? (
                  isOk ? (
                    <Check className="size-4 shrink-0 text-emerald-500 animate-in zoom-in-50" />
                  ) : (
                    <X className="size-4 shrink-0 text-destructive animate-in zoom-in-50" />
                  )
                ) : (
                  <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
                )}
                <span className="flex-1 truncate text-sm font-medium text-foreground">
                  {machine.name}
                </span>
                {isDone && !isOk && (
                  <span className="ml-auto truncate text-xs text-muted-foreground">
                    {result.error}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {!isPending && (
          <DialogFooter>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className={cn(
                failed > 0
                  ? "bg-amber-600 text-white hover:bg-amber-500"
                  : "bg-emerald-600 text-white hover:bg-emerald-500",
              )}
            >
              Done
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
